<script setup>
import { ref, unref } from 'vue';
import { getDateWithoutTime } from '@/helpers/dateFormatHelper.js';
import { MODAL_ORDERS_TABLE } from '@/data/constants/modalConstants.js';
import { useModalTables } from '@/composables/modalTables.js';
import { createToaster } from '@meforma/vue-toaster';
import { isNotEmptyArray } from '../../helpers/utilsHelper';
import ModalBase from '@/components/utils/ModalBase.vue';
import ModalBody from '@/components/utils/ModalBody.vue';
import { useTableColumns } from '../../composables/tableColumns.js';
import ColumnsVisibilityDropdown from '../utils/ColumnsVisibilityDropdown.vue';

const props = defineProps({
  initialOrders: { type: Array, default: () => new Array() }
});

const initialColumns = [
  {
    key: 'order_number',
    header: 'Nr zlecenia',
    thStyle: 'white-space: nowrap;',
    tdStyle: 'white-space: nowrap;'
  },
  {
    key: 'direction',
    header: 'Rodzaj zlecenia',
    thStyle: 'white-space: nowrap;',
    tdStyle: 'white-space: nowrap; text-align: center;'
  },
  {
    key: 'gmina.name',
    header: 'Gmina',
    thStyle: 'white-space: nowrap;',
    tdStyle: 'white-space: nowrap;'
  },
  {
    key: 'region.name',
    header: 'Region',
    thStyle: 'white-space: nowrap;',
    tdStyle: 'white-space: nowrap;'
  },
  {
    key: 'address',
    header: 'Lokalizacja',
    thStyle: 'white-space: nowrap;',
    tdStyle: 'white-space: nowrap;'
  },
  {
    key: 'coordinator',
    header: 'Koordynator',
    tdStyle: 'white-space: nowrap;'
  },
  {
    key: 'receive_date',
    header: 'Wpłynęło do RADO',
    thStyle: 'white-space: nowrap;',
    tdStyle: 'white-space: nowrap;'
  },
  {
    key: 'deadline',
    header: 'Termin realizacji',
    thStyle: 'white-space: nowrap;',
    tdStyle: 'white-space: nowrap;'
  },
  {
    key: 'order_status',
    header: 'Status zlecenia',
    thStyle: 'white-space: nowrap; text-align: center;',
    tdStyle: 'white-space: nowrap; text-align: center;'
  }
];

const {
  accessibleColumns,
  visibleColumns,
  isSettingsSaving,
  handleColumnVisibilityChange,
  saveColumnsSettings,
  getNestedValue,
  getColumnThStyle,
  getColumnTdStyle
} = useTableColumns('geodetic_protocol_aggregate_assigning', initialColumns);

const toaster = createToaster({ position: 'top-right', duration: 3000 });
const modalBase = ref(null);
const { modal, showModal } = useModalTables(modalBase);
const orders = ref(null);

function onModalCompleted(data, hideModal = true) {
  if (hideModal) {
    modalBase.value.hide();
  }

  switch (modal.value.type) {
    case MODAL_ORDERS_TABLE:
      data.canBeDeleted = true;
      if (orders.value.some((x) => x.id != null && x.id === data.id)) {
        toaster.Add('Wybrane zlecenie jest już na liście.', { type: 'warning' });
      } else {
        orders.value.push(data);
      }
      break;
  }
}

function deleteItemFromList(orderToDelete) {
  orders.value = orders.value.filter((x) => x.id != null && x.id !== orderToDelete.id);
}

// created
orders.value = unref(props.initialOrders);

defineExpose({ orders });
</script>

<template>
  <div class="ibox">
    <div class="ibox-title">
      <h5>Zlecenia, do których dodany zostanie protokół geodezji</h5>
      <div class="ibox-tools">
        <a
          class="btn btn-primary btn-xs"
          @click="
            showModal(MODAL_ORDERS_TABLE, {
              additionalProps: {
                initialUserFilter: { id_order_type: [2, 3] }
              }
            })
          "
          >+ Dodaj zlecenie do listy</a
        >
      </div>
    </div>
    <div class="ibox-content">
      <div v-if="isNotEmptyArray(orders)" class="table-responsive">
        <table class="table table-striped">
          <thead>
            <tr>
              <template v-for="column in visibleColumns" :key="column.key">
                <th
                  :style="getColumnThStyle(column)"
                  @click="column.sortable ? sortBy(column.sortKey) : undefined"
                >
                  {{ column.header }}
                  <span
                    v-if="column.sortable"
                    v-html="getSortingIcon(currentSort, column.sortKey)"
                  />
                </th>
              </template>
              <th></th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="order in orders" :key="order.id">
              <template v-for="column in visibleColumns" :key="column.key">
                <td :style="getColumnTdStyle(column)">
                  <!-- Direction column -->
                  <template v-if="column.key === 'direction'">
                    <span
                      class="label label-primary"
                      :style="
                        order?.direction?.color != null
                          ? 'background-color:' + order.direction.color
                          : ''
                      "
                    >
                      {{ order?.direction?.name ?? '-' }}</span
                    >
                  </template>

                  <!-- Coordinator column -->
                  <template v-else-if="column.key === 'coordinator'">
                    <span
                      v-if="
                        order?.coordinator?.firstName == null ||
                        order?.coordinator?.lastName == null
                      "
                    >
                      -
                    </span>
                    <span v-else>
                      {{ order.coordinator.firstName + ' ' + order.coordinator.lastName }}
                    </span>
                  </template>

                  <!-- Receive date column -->
                  <template v-else-if="column.key === 'receive_date'">
                    {{ order?.receive_date != null ? getDateWithoutTime(order.receive_date) : '-' }}
                  </template>

                  <!-- Deadline column -->
                  <template v-else-if="column.key === 'deadline'">
                    {{ order?.deadline != null ? getDateWithoutTime(order.deadline) : '-' }}
                  </template>

                  <!-- Order status column -->
                  <template v-else-if="column.key === 'order_status'">
                    <span
                      class="label label-primary"
                      :style="
                        order?.order_status?.color != null
                          ? 'background-color:' + order.order_status.color
                          : ''
                      "
                    >
                      {{ order?.order_status?.name ?? '-' }}</span
                    >
                  </template>

                  <!-- Default column rendering -->
                  <template v-else>
                    {{
                      column.key.includes('.')
                        ? getNestedValue(order, column.key) ?? '-'
                        : order[column.key] ?? '-'
                    }}
                  </template>
                </td>
              </template>

              <td style="white-space: nowrap">
                <div class="float-right">
                  <button
                    v-if="order.canBeDeleted"
                    class="btn btn-outline btn-primary btn-xs"
                    type="button"
                    @click="deleteItemFromList(order)"
                    onclick="event.stopPropagation()"
                  >
                    <span>Usuń</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else>
        <p class="text-center mt-3">Brak elementów do wyświetlenia</p>
      </div>

      <div class="row mt-3">
        <div class="col-xl-3">
          <ColumnsVisibilityDropdown
            :columns="accessibleColumns"
            :isSettingsSaving="isSettingsSaving"
            @save="saveColumnsSettings"
            @showAll="handleColumnVisibilityChange('all')"
            @change="handleColumnVisibilityChange"
          />
        </div>
      </div>
    </div>

    <ModalBase ref="modalBase">
      <ModalBody>
        <template v-slot:title>
          {{ modal.title }}
        </template>
        <template v-slot:body>
          <component
            :is="modal.component"
            @on-completed="onModalCompleted"
            :key="modal.key"
            v-bind="modal.properties"
          ></component>
        </template>
      </ModalBody>
    </ModalBase>
  </div>
</template>

<style></style>
